import { Either, Option, ReadonlyArray } from "@morphism/fp";
import React from "react";
import { CSVLink } from "react-csv";

import { Button as GenericButton } from "~/ButtonV2";
import { Driver } from "~/Driver";
import { DVIR, DVIRs } from "~/DVIR";
import * as Icon from "~/Icon";
import { Vehicle } from "~/Log/Summary/Table/Vehicle";
import { Time } from "~/Time";
import { DateTime } from "~/Time/DateTime";

import { Type } from "../Type";

import { Defects } from "./Table/Defects";

export type Download = {
  readonly Date: string;
  readonly "Date Created": string;
  readonly Type: string;
  readonly Status: string;
  readonly Driver: string;
  readonly Vehicle: string;
  readonly "Vehicle Defects": string;
  readonly Trailers: string;
  readonly "Trailer Defects": string;
};

export function Download({
  dvirs,
  settings,
}: {
  readonly dvirs: DVIRs;
  readonly settings: DVIRs.Table.Settings;
}) {
  return pipe(
    dvirs,
    (all) => DVIRs.applyAllFilters(all, settings),
    ([first, second, ...rest]) =>
      first && second ? (
        <Download.CSV.Link dvirs={[first, second, ...rest]} />
      ) : (
        <Download.Button disabled />
      )
  );
}

export namespace Download {
  export type CSV = readonly Download[];
  export namespace CSV {
    export const fromDVIRs = (dvirs: DVIRs) =>
      pipe(dvirs, ReadonlyArray.map(Row.fromDVIR));

    export const Link = ({ dvirs }: { readonly dvirs: DVIRs }) =>
      pipe(dvirs, Download.CSV.fromDVIRs, ([first, ...rest]) => (
        <CSVLink
          target="_blank"
          headers={Object.keys(first)}
          data={[first, ...rest]}
          filename={pipe(
            Time.DateTime.now(),
            Time.DateTime.toDateOnlyMMMDYYYY,
            (dateTime) => `DVIRs – ${dateTime}.csv`
          )}
        >
          <Download.Button />
        </CSVLink>
      ));

    export namespace Row {
      export const fromDVIR = (dvir: DVIR): Download => ({
        Date: pipe(
          dvir.lifecycle.submitted.dateTime,
          DateTime.fromNumber,
          DateTime.toDateOnly
        ),
        "Date Created": pipe(dvir.lifecycle.created.dateTime, (dt) => {
          const dateTime = DateTime.fromNumber(dt);
          return `${DateTime.toDateOnly(dateTime)} ${DateTime.toHMM(dateTime)}`;
        }),
        Type: Type.toString(dvir),
        Status: DVIR.isResolved(dvir) ? "Resolved" : DVIR.isNoDefects(dvir) ? "No Defects" : "Unresolved",
        Driver: pipe(
          dvir.driver,
          Either.fold(() => "", Driver.formatStringForExport)
        ),
        Vehicle: pipe(
          dvir.vehicle,
          Either.fold(() => Option.none, Option.fromNullable),
          Option.fold(() => "N/A", Vehicle.formatStringForExport)
        ),
        "Vehicle Defects": Defects.forType("vehicle")(dvir)
          .map((d) => d.defect.type.name)
          .join(", "),
        Trailers: dvir.trailers
          .map((t) =>
            pipe(
              t,
              Either.fold(() => "", Vehicle.formatStringForExport)
            )
          )
          .join(", "),
        "Trailer Defects": Defects.forType("trailers")(dvir)
          .map((d) => d.defect.type.name)
          .join(", "),
      });
    }
  }

  export const Button = (props: { readonly disabled?: boolean }) => (
    <GenericButton icon={<Icon.FileDownload />} {...props}>
      Download
    </GenericButton>
  );
}
